<script setup>
  import { computed, ref, watch } from "vue";
  import { useFetch } from "@/composables/useFetch.js";
  import { useWalletStore } from "@/stores/walletStore.js";
  import { uri } from "@/constants/config.js";
  import Lucide from "@/base-components/Lucide";
  import { useCountryFlag } from "@/composables/useCountryFlag.js";
  import { Tab } from "@/base-components/Headless";
  import { object } from "yup";
  import useValidate from "@/composables/useValidate.js";
  import { Form } from "vee-validate";
  import InputText from "@/base-components/Form/InputText.vue";
  import Button from "@/base-components/Button";
  import { FormCheck, FormSelect } from "@/base-components/Form";
  import { useRouter } from "vue-router";
  import Alert from "@/base-components/Alert";
  import {
    currencyReadable,
    formatNumber,
    formatSize,
  } from "@/composables/utility.js";

  const router = useRouter();
  const loadingFlag = ref(false);
  const loadingRegionListFlag = ref(true);
  const regionSelected = ref(null);
  const regionsList = ref([]);
  const flavorSelected = ref(null);
  const imageSelected = ref(null);
  const networkSelected = ref(null);
  const aggregateList = ref([]);
  const flags = ref([]);
  const paymentCycle = ref("1");
  const networkTypeList = ref(null);
  const networkTypeSelected = ref(null);
  const networkOptionSelected = ref(null);
  const requestParams = ref({
    regionId: null,
    name: null,
    hostname: null,
    flavorId: null,
    imageId: null,
    netId: null,
    paymentCycle: null,
    networkPriceId: null,
    priceKey: null,
  });
  const validationSchema = object(useValidate(["server_name", "hostname"]));
  const price = ref(null);
  const ipPrice = ref(null);
  const ramPrice = ref(null);
  const volumePrice = ref(null);
  const cpuPrice = ref(null);
  const networkPrice = ref(null);
  const nullPrice = { key: null, price: 0 };
  const loadingCreateVMFlag = ref(false);
  const walletStore = useWalletStore();

  const hasDeposit = computed(() =>
    walletStore.wallet ? walletStore.wallet.amount >= price.value : true
  );

  async function getRegionsList() {
    loadingRegionListFlag.value = true;
    try {
      const { data, error } = await useFetch(uri.api.iaas.region.list, {
        method: "GET",
      });
      if (error) {
      } else {
        data.forEach((item, index) => {
          const { flag, getFlag } = useCountryFlag();
          getFlag(item.country);
          flags.value[index] = flag.value;
        });
        regionsList.value = data;
      }
    } catch (err) {
    } finally {
      loadingRegionListFlag.value = false;
    }
  }

  async function getAggregateList() {
    loadingFlag.value = true;
    const tagHelper = (tags) => {
      return tags.reduce((acc, cur) => {
        const data = cur.split(":");
        acc[data.shift() || cur] = data.join(":");
        return acc;
      }, {});
    };
    try {
      const { data, error } = await useFetch(
        `${uri.api.iaas.self}${regionSelected.value.id}`,
        { method: "GET" }
      );
      if (error) {
      } else {
        try {
          let images = [];
          let flatImages = [];
          data.images.forEach((v) => {
            const tags = tagHelper(v.tags);
            tags["name"] = tags.name || v.name;
            tags["version"] = tags.version || v.name;
            let item = images.find((x) => x.name === tags.name);
            if (!item) {
              item = {
                ...tags,
                logo: `/image/icons/os/${tags.name.toLowerCase()}.svg`,
                version: [],
              };
              images.push(item);
            }
            flatImages.push({ ...v, ...tags });
            item.version.push({ ...v, ...tags });
          });
          data.images = images;
          data.flatImages = flatImages;
          data.flavors = data.flavors.sort((a, b) =>
            a.name > b.name ? 1 : -1
          );
        } catch (e) {
          console.log(e);
        }
        aggregateList.value = data;
      }
    } catch (err) {
    } finally {
      loadingFlag.value = false;
    }
  }

  function ram(value) {
    value = value ?? 0;
    if (value > 1000) {
      return `${value / 1024} GB`;
    } else {
      return `${value} MB`;
    }
  }

  function activeClass(isSelected) {
    return isSelected
      ? "before:bg-primary before:mt-1 before:shadow-primary opacity-100 border-primary/60 border-2"
      : "opacity-80";
  }

  function activeShadow(isSelected) {
    return isSelected
      ? "before:bg-primary/60 before:mt-1 before:shadow-primary"
      : "";
  }

  function setFirstImage(image) {
    if (imageSelected.value && imageSelected.value.name === image.name) return;
    setImage(image.version[0].id);
  }

  function setImage(imageId) {
    const image = aggregateList.value.flatImages.find(
      (x) => x.id === imageId
    ) || { status: "inactive" };
    if (image.status !== "active") {
      return;
    }
    if (flavorSelected.value) {
      if (
        !(
          Math.ceil(image.virtual_size / Math.pow(1024, 3)) <=
          flavorSelected.value.disk
        )
      ) {
        flavorSelected.value = null;
      }
    }
    imageSelected.value = image;
  }

  function setFlavor(flavor) {
    if (
      Math.ceil(imageSelected.value.virtual_size / Math.pow(1024, 3)) <=
      flavor.disk
    ) {
      flavorSelected.value = flavor;
    }
  }

  async function onSubmitCreateVM() {
    loadingCreateVMFlag.value = true;
    requestParams.value = {
      regionId: Number(regionSelected.value.id),
      name: requestParams.value.name,
      hostname: requestParams.value.hostname,
      flavorId: flavorSelected.value.id,
      imageId: imageSelected.value.id,
      netId: Number(networkSelected.value.id),
      paymentCycle: Number(paymentCycle.value),
      networkPriceId: Number(networkTypeSelected.value.id),
      priceKey: networkPrice.value.key,
    };
    try {
      // Await the result of useFetch
      const response = await useFetch(uri.api.iaas.instance.create, {
        body: requestParams.value,
      });
      if (response.error) {
      } else {
        await Promise.all([walletStore.fetchWallet(), router.push("/vm")]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      loadingCreateVMFlag.value = false;
    }
  }

  function getPriceObject(param) {
    if (!aggregateList.value || !aggregateList.value.pricing) return 0;
    if (aggregateList.value.pricing.length === 0) return 0;
    return aggregateList.value.pricing
      .find((x) => x.assetType === param.assetType)
      .pricePlan.value.find((x) => x.key === paymentCycle.value)
      .value.find((x) => x.key === param.value);
  }

  function updatePrice() {
    price.value = 0;
    if (imageSelected.value !== null) {
      ipPrice.value =
        getPriceObject({ assetType: "IPV4", value: "1" }) ?? nullPrice;
      price.value += ipPrice.value.price;
    }
    if (flavorSelected.value !== null) {
      ramPrice.value =
        getPriceObject({
          assetType: "RAM",
          value: flavorSelected.value.ram.toString(),
        }) ?? nullPrice;
      volumePrice.value =
        getPriceObject({
          assetType: "Volume",
          value: flavorSelected.value.disk.toString(),
        }) ?? nullPrice;
      cpuPrice.value =
        getPriceObject({
          assetType: "CPU",
          value: flavorSelected.value.vcpus.toString(),
        }) ?? nullPrice;
      price.value +=
        ramPrice.value.price + volumePrice.value.price + cpuPrice.value.price;
    }
    if (networkOptionSelected.value !== null) {
      networkPrice.value =
        getPriceObject({
          assetType: networkTypeSelected.value.assetType,
          value: networkOptionSelected.value.key.toString(),
        }) ?? nullPrice;
      price.value += networkPrice.value.price;
    }
  }

  function flavorPrice(flavor) {
    const r =
      getPriceObject({ assetType: "RAM", value: flavor.ram.toString() }) ??
      nullPrice;
    const v =
      getPriceObject({ assetType: "Volume", value: flavor.disk.toString() }) ??
      nullPrice;
    const c =
      getPriceObject({ assetType: "CPU", value: flavor.vcpus.toString() }) ??
      nullPrice;
    return r.price + v.price + c.price;
  }

  getRegionsList();

  function selectRegion(region) {
    if (!region.isActive) return;
    regionSelected.value = region;
  }

  watch(regionSelected, (_region) => {
    aggregateList.value = [];
    networkSelected.value = null;
    flavorSelected.value = null;
    imageSelected.value = null;
    loadingFlag.value = true;
    updatePrice();
    getAggregateList();
  });

  watch(flavorSelected, (_flavor, _oldFlavor) => {
    updatePrice();
  });

  watch(networkSelected, (network) => {
    if (network) {
      networkOptionSelected.value = null;
      networkTypeList.value = aggregateList.value.pricing.filter(
        (x) =>
          ["Traffic", "Bandwidth"].includes(x.assetType) &&
          x.networkId === network.id
      );
    } else {
      networkOptionSelected.value = null;
      networkTypeList.value = [];
    }
  });

  watch(networkTypeSelected, (_networkType) => {
    networkOptionSelected.value = null;
  });

  watch(paymentCycle, (_paymentCycle, _oldValue) => {
    if (networkOptionSelected.value !== null) {
      const networkPlans =
        networkTypeSelected.value.pricePlan.value.find(
          (item) => item.key === paymentCycle.value
        )?.value ?? [];
      networkOptionSelected.value = networkPlans.find(
        (x) => x.key === networkOptionSelected.value.key
      );
    }
    updatePrice();
  });

  watch(imageSelected, () => {
    updatePrice();
  });

  watch(networkOptionSelected, () => {
    updatePrice();
  });
</script>

<template>
  <div class="relative xl:flex justify-end xl:overflow-auto xl:max-h-screen">
    <div class="xl:w-3/4 justify-self-start grid grid-cols-12 gap-6">
      <div class="z-20 col-span-12">
        <div class="grid grid-cols-12 mb-3 sm:gap-10 intro-y">
          <div class="col-span-12">
            <!--Start Region Box-->
            <div class="my-2">
              <div
                class="rounded-md border-slate-200/60 dark:border-darkmode-400"
              >
                <div
                  class="p-4 border-b border-slate-200/60 dark:border-darkmode-400 font-medium text-base leading-none text-slate-500"
                >
                  {{ $t("title.regions") }}
                </div>
                <div
                  v-if="!loadingRegionListFlag"
                  class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 min-h-20"
                >
                  <div v-for="(region, index) in regionsList">
                    <div
                      class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                      @click="selectRegion(region)"
                      :class="activeShadow(region === regionSelected)"
                    >
                      <div
                        class="p-2 box"
                        :class="activeClass(region === regionSelected)"
                      >
                        <div class="relative">
                          <div
                            class="absolute left-0 top-0 text-muted text-sm bg-gray-100"
                            v-if="!region.isActive"
                          >
                            {{ $t("title.inactive") }}
                          </div>
                          <div class=" ">
                            <div class="h-10 w-10">
                              <img
                                v-if="flags[index]"
                                :src="flags[index]"
                                :alt="`Flag of ${region.country}`"
                                :class="{
                                  'grayscale-[70%]': regionSelected
                                    ? region !== regionSelected
                                    : true,
                                }"
                              />
                            </div>
                            <div class="p-2">
                              <h2 class="font-medium truncate">
                                {{ region.displayName }}
                              </h2>
                            </div>
                            <div class="p-2 absolute bottom-0 end-0">
                              <span
                                class="px-2 rounded-md bg-secondary border border-transparent text-xs transition-all shadow-sm"
                              >
                                {{ region.name }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="min-h-20 flex justify-center items-center">
                  <Lucide
                    icon="Loader"
                    class="w-6 h-5 text-black animate-spin"
                  />
                </div>
              </div>
            </div>
            <!--End Regions Box-->
            <div v-if="regionSelected">
              <!--Start Images Box-->
              <div class="my-2">
                <div
                  class="rounded-md border-slate-200/60 dark:border-darkmode-400"
                >
                  <div
                    class="p-4 border-b border-slate-200/60 dark:border-darkmode-400 font-medium text-base leading-none text-slate-500"
                  >
                    {{ $t("title.os-type") }}
                  </div>
                  <div
                    v-if="!loadingFlag"
                    class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20"
                  >
                    <div
                      v-if="aggregateList.images"
                      v-for="image in aggregateList.images"
                      class=" "
                      dir="ltr"
                    >
                      <div
                        class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                        :class="
                          activeShadow(
                            imageSelected && image.name === imageSelected.name
                          )
                        "
                        @click="setFirstImage(image)"
                      >
                        <div
                          class="pt-2 box"
                          :class="
                            ({
                              'bg-gray-100 text-gray-700':
                                image.status !== 'active',
                            },
                            activeClass(
                              imageSelected && image.name === imageSelected.name
                            ))
                          "
                        >
                          <div class="flex-row justify-between w-full">
                            <div class="h-16 w-16 p-2 items-center mx-auto">
                              <img
                                v-if="image.logo"
                                :src="image.logo"
                                :alt="`${image.name}`"
                                class="flag-icon"
                              />
                            </div>
                          </div>
                          <div class="mx-auto sm:w-auto p-4">
                            <div class="">
                              <h2 class="mr-5 font-medium truncate">
                                {{ image.os_type }}
                              </h2>
                            </div>
                            <div class="w-full">
                              <h2 class="mr-5 font-medium truncate">
                                {{ image.name }}
                              </h2>
                              <FormSelect
                                v-if="
                                  imageSelected &&
                                  imageSelected.name === image.name
                                "
                                @update:model-value="setImage"
                              >
                                <option
                                  :value="vImg.id"
                                  :disabled="vImg.status !== 'active'"
                                  v-for="vImg in image.version"
                                  :key="vImg.id"
                                  :selected="imageSelected.id === vImg.id"
                                >
                                  {{ vImg.version }}
                                </option>
                              </FormSelect>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="min-h-20 flex justify-center items-center">
                    <Lucide
                      icon="Loader"
                      class="w-6 h-5 text-black animate-spin"
                    />
                  </div>
                </div>
              </div>
              <!--End Images Box-->

              <div v-if="imageSelected">
                <!--Start Flavors Box-->
                <div class="my-2">
                  <div
                    class="rounded-md border-slate-200/60 dark:border-darkmode-400"
                  >
                    <div
                      class="p-4 border-b border-slate-200/60 dark:border-darkmode-400 font-medium text-base leading-none text-slate-500"
                    >
                      {{ $t("title.flavors") }}
                    </div>
                    <div
                      class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20"
                    >
                      <div
                        v-if="aggregateList.flavors"
                        v-for="flavor in aggregateList.flavors"
                        class="ltr"
                        dir="ltr"
                      >
                        <div
                          class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                          @click="setFlavor(flavor)"
                          :class="activeShadow(flavor === flavorSelected)"
                        >
                          <div
                            class="p-5 box"
                            :class="
                              ({
                                'bg-gray-100 text-gray-700': !(
                                  Math.ceil(
                                    imageSelected.virtual_size /
                                      Math.pow(1024, 3)
                                  ) <= flavor.disk
                                ),
                              },
                              activeClass(flavor === flavorSelected))
                            "
                          >
                            <div
                              class="mx-auto sm:w-auto grid grid-cols-1 gap-2"
                            >
                              <div
                                class="flex items-center border-b mb-2 pb-2 gap-3"
                              >
                                <Lucide icon="Server" class="w-4 h-4" />
                                <h2 class="mr-5 font-medium truncate">
                                  {{ flavor.name }}
                                </h2>
                              </div>
                              <div class="flex items-center gap-3">
                                <Lucide icon="Cpu" class="w-4 h-4" />
                                <h2 class="mr-5 font-medium truncate">
                                  {{ `${flavor.vcpus} Core` }}
                                </h2>
                              </div>
                              <div class="flex items-center gap-3">
                                <Lucide icon="Archive" class="w-4 h-4" />
                                <h2 class="mr-5 font-medium truncate">
                                  {{ ram(flavor.ram) }}
                                </h2>
                              </div>
                              <div class="flex items-center gap-3">
                                <Lucide icon="HardDrive" class="w-4 h-4" />
                                <h2 class="mr-5 font-medium truncate">
                                  {{ `${flavor.disk} GB` }}
                                </h2>
                              </div>
                            </div>

                            <div
                              class="flex items-center border-t mb-t pt-2 gap-3"
                            >
                              <Lucide icon="DollarSign" class="w-4 h-4" />
                              <h2 class="mr-5 font-medium truncate" dir="rtl">
                                {{ formatNumber(flavorPrice(flavor)) }}
                                ریال
                              </h2>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!--End Flavors Box-->

                <!--Start Network Box-->
                <div class="my-2">
                  <div
                    class="rounded-md border-slate-200/60 dark:border-darkmode-400"
                  >
                    <div
                      class="p-4 border-b border-slate-200/60 dark:border-darkmode-400 font-medium text-base leading-none text-slate-500"
                    >
                      {{ $t("title.networks") }}
                    </div>
                    <div
                      class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20"
                    >
                      <div
                        v-if="aggregateList.networks"
                        v-for="network in aggregateList.networks"
                        class="ltr"
                        dir="ltr"
                      >
                        <div
                          class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                          @click="
                            network.isActive
                              ? (networkSelected = network)
                              : null
                          "
                          :class="activeShadow(network === networkSelected)"
                        >
                          <div
                            class="p-5 box"
                            :class="
                              (activeClass(network === networkSelected),
                              {
                                'bg-gray-100 text-gray-700': !network.isActive,
                              })
                            "
                          >
                            <div class="relative">
                              <div
                                class="absolute -right-3 -top-5 text-muted text-sm bg-gray-300"
                                v-if="!network.isActive"
                              >
                                {{ $t("title.inactive") }}
                              </div>
                            </div>
                            <div class="mx-auto sm:w-auto">
                              <div class="flex items-center">
                                <h2 class="mr-5 font-medium truncate">
                                  <Lucide
                                    icon="EthernetPort"
                                    class="inline-block"
                                  />
                                  {{ network.displayName }}
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!--End Network Box-->

                <!--Start Network Type Box-->
                <div class="my-2" v-if="networkSelected">
                  <div
                    class="rounded-md border-slate-200/60 dark:border-darkmode-400"
                  >
                    <div
                      class="p-4 border-b border-slate-200/60 dark:border-darkmode-400 font-medium text-base leading-none text-slate-500"
                    >
                      {{ $t("title.network-type") }}
                    </div>
                    <div
                      class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20"
                    >
                      <div
                        v-if="networkTypeList"
                        v-for="networkType in networkTypeList"
                        class="ltr"
                        dir="ltr"
                      >
                        <div
                          class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                          @click="networkTypeSelected = networkType"
                          :class="
                            activeShadow(networkType === networkTypeSelected)
                          "
                        >
                          <div
                            class="p-5 box"
                            :class="
                              activeClass(networkType === networkTypeSelected)
                            "
                          >
                            <div class="mx-auto sm:w-auto">
                              <div class="flex items-center">
                                <h2 class="mr-5 font-medium truncate">
                                  <Lucide
                                    :icon="
                                      networkType.assetType === 'Bandwidth'
                                        ? 'AudioLines'
                                        : 'ArrowUpDown'
                                    "
                                    class="inline-block"
                                  />
                                  {{ networkType.assetName }}
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Alert
                      v-if="networkTypeSelected"
                      variant="outline-info"
                      class="flex items-center mb-2"
                      v-slot="{ dismiss }"
                    >
                      <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2" />
                      ترافیک خروجی رایگان می‌باشد
                      <Alert.DismissButton
                        type="button"
                        class="btn-close left-0 right-auto"
                        @click="dismiss"
                        aria-label="Close"
                      >
                        <Lucide icon="X" class="w-4 h-4" />
                      </Alert.DismissButton>
                    </Alert>
                    <div
                      v-if="networkTypeSelected"
                      class="grid grid-cols-1 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-3 py-3 px-3 pb-4 min-h-20"
                    >
                      <div
                        v-for="option in networkTypeSelected.pricePlan.value.find(
                          (item) => item.key === paymentCycle
                        )?.value ?? []"
                        class="ltr"
                        dir="ltr"
                      >
                        <div
                          class="cursor-pointer before:hidden xl:before:block intro-y relative before:content-[''] before:bg-white/60 before:w-[90%] before:h-full before:absolute before:rounded-md before:mx-auto before:inset-x-0 before:dark:bg-darkmode-400/70"
                          @click="networkOptionSelected = option"
                          :class="
                            activeShadow(option === networkOptionSelected)
                          "
                        >
                          <div
                            class="px-5 py-2 box"
                            :class="
                              activeClass(option === networkOptionSelected)
                            "
                          >
                            <div class="grid grid-cols-1 gap-2">
                              <div class="flex items-center">
                                <h2 class="mr-5 font-bold truncate">
                                  {{ formatSize(option.key, "mb", 0) }}
                                </h2>
                              </div>
                              <div class="flex items-center">
                                <h2 class="mr-5 text-xs truncate" dir="rtl">
                                  {{ formatNumber(option.price) }}
                                  ریال
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!--End Type Box-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="xl:w-1/4 z-30 grid xl:block top-0 w-full xl:w-fit h-full grid-cols-10 gap-6 pb-6 -mt-8 xl:mt-0 xl:z-auto xl:sticky xl:top-0"
    >
      <div class="z-30 col-span-12 xl:col-start-10 xl:pb-16">
        <div class="flex flex-col h-full relative">
          <div
            class="p-2 mt-6 box bg-secondary border-dotted border-primary/60 intro-x"
          >
            <div class="flex flex-row">
              <div
                class="items-start font-bold p-3 justify-center text-base h-12 dark:bg-darkmode-300 bg-opacity-20 hover:bg-opacity-30"
              >
                {{ $t("title.currency-mount") }}
              </div>
              <div class="w-full" dir="ltr">
                <div class="text-xl font-medium leading-5 ps-1 mt-3.5">
                  {{ formatNumber(price) ?? "-" }}
                </div>
              </div>
              <div
                class="w-14 p-3 justify-center text-base h-12 dark:bg-darkmode-300 bg-opacity-20 hover:bg-opacity-30"
              >
                {{ $t("title.currency-unit") }}
              </div>
            </div>
          </div>

          <div class="xl:min-h-0 intro-x box p-5 mt-1 sticky">
            <Tab.Group class="max-h-full xl:overflow-y-auto" :default-index="3">
              <div class="">
                {{ $t("title.payment-cycle") }}
              </div>
              <div class="top-0 pb-6 xl:sticky">
                <Tab.List
                  variant="pills"
                  class="p-1 mx-auto mt-5 border border-dashed rounded-md border-slate-300 dark:border-darkmode-300"
                >
                  <Tab>
                    <Tab.Button
                      as="button"
                      class="w-full py-1.5 px-2"
                      @click="paymentCycle = '12'"
                    >
                      1 {{ $t("title.year") }}
                    </Tab.Button>
                  </Tab>
                  <Tab>
                    <Tab.Button
                      as="button"
                      class="w-full py-1.5 px-2"
                      @click="paymentCycle = '6'"
                    >
                      6 {{ $t("title.month") }}
                    </Tab.Button>
                  </Tab>
                  <Tab>
                    <Tab.Button
                      as="button"
                      class="w-full py-1.5 px-2"
                      @click="paymentCycle = '3'"
                    >
                      3 {{ $t("title.month") }}
                    </Tab.Button>
                  </Tab>
                  <Tab>
                    <Tab.Button
                      as="button"
                      class="w-full py-1.5 px-2"
                      @click="paymentCycle = '1'"
                    >
                      1 {{ $t("title.month") }}
                    </Tab.Button>
                  </Tab>
                </Tab.List>
              </div>
            </Tab.Group>
            <Form
              class="intro-y relative grid grid-cols-2 gap-8"
              :validation-schema="validationSchema"
              @submit="onSubmitCreateVM"
            >
              <div
                class="intro-y col-span-2 md:col-span-1 xl:col-span-2 lg:col-span-2"
              >
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!regionSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="regionSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.regions") }}
                    </div>
                    <div v-if="regionSelected" dir="ltr">
                      {{ regionSelected.displayName ?? "" }}
                    </div>
                  </div>
                </div>
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!imageSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="imageSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.os-type") }}
                    </div>
                    <div v-if="imageSelected" dir="ltr">
                      {{ imageSelected.name ?? "" }} -
                      {{ imageSelected.version ?? "" }}
                    </div>
                  </div>
                </div>
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!flavorSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="flavorSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.flavors") }}
                    </div>
                    <div v-if="flavorSelected" dir="ltr">
                      {{ flavorSelected.name ?? "" }}
                    </div>
                  </div>
                </div>
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!networkSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="networkSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.networks") }}
                    </div>
                    <div v-if="networkSelected" dir="ltr">
                      {{ networkSelected.name ?? "" }}
                    </div>
                  </div>
                </div>
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!networkTypeSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="networkTypeSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.network-type") }}
                    </div>
                    <div v-if="networkTypeSelected" dir="ltr">
                      {{ networkTypeSelected.assetName ?? "" }}
                    </div>
                  </div>
                </div>
                <div class="mt-2 flex">
                  <Lucide
                    v-if="!networkOptionSelected"
                    icon="MinusSquareIcon"
                    color="gray"
                  />
                  <Lucide
                    v-if="networkOptionSelected"
                    icon="CheckSquareIcon"
                    color="green"
                  />
                  <div class="grid grid-cols-2 gap-2 ps-2">
                    <div class="min-w-24">
                      {{ $t("title.network-mount") }}
                    </div>
                    <div v-if="networkOptionSelected" dir="ltr">
                      {{ formatSize(networkOptionSelected.key, "mb") }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="intro-y col-span-2 md:col-span-1 xl:col-span-2 lg:col-span-2"
              >
                <FormCheck class="mt-2">
                  <FormCheck.Input
                    :disabled="true"
                    checked
                    id="radio-switch-1"
                    type="radio"
                    name="vertical_radio_button"
                    value="vertical-radio-chris-evans"
                  />
                  <FormCheck.Label htmlFor="radio-switch-1 ms-2">
                    <span class="ps-2 text-sm font-bold">IPV4</span>
                  </FormCheck.Label>
                </FormCheck>
                <FormCheck class="mt-2">
                  <FormCheck.Input
                    :disabled="true"
                    id="radio-switch-2"
                    type="radio"
                    name="vertical_radio_button"
                    value="vertical-radio-chris-evans"
                  />
                  <FormCheck.Label htmlFor="radio-switch-2 ms-2">
                    <span class="ps-2">IPV6</span>
                  </FormCheck.Label>
                </FormCheck>
              </div>
              <div
                class="intro-y col-span-2 md:col-span-1 xl:col-span-2 lg:col-span-2"
              >
                <InputText
                  :dir="'ltr'"
                  name="server_name"
                  :has-label="true"
                  :label="$t('label.server-name')"
                  v-model="requestParams.name"
                  id="input-wizard-3"
                  type="text"
                />
              </div>
              <div
                class="intro-y col-span-2 md:col-span-1 xl:col-span-2 lg:col-span-2"
              >
                <InputText
                  :dir="'ltr'"
                  name="hostname"
                  :has-label="true"
                  :label="$t('label.hostname')"
                  v-model="requestParams.hostname"
                  id="input-wizard-3"
                  type="text"
                />
              </div>
              <div
                class="intro-y relative col-span-2 items-end justify-end mt-2 border-top"
              >
                <FreeStyleShimmer
                  height="30px"
                  width="100%"
                  v-if="loadingCreateVMFlag"
                />
                <div v-if="!hasDeposit" class="p-5 mt-6 box bg-warning intro-x">
                  <div class="flex flex-wrap gap-3">
                    <div class="mr-auto">
                      <div
                        class="flex items-center leading-3 text-white text-opacity-70 dark:text-slate-300"
                      >
                        {{ $t("title.insufficient-balance") }}
                      </div>
                      <div
                        class="text-white relative text-2xl font-medium leading-5 pl-4 mt-3.5"
                      >
                        <span class="absolute text-xl top-0 left-0 -mt-1.5">
                        </span>
                        {{ $t("title.fast-charge") }}
                      </div>
                    </div>
                    <a
                      class="flex items-center justify-center w-12 h-12 text-white bg-white rounded-full dark:bg-darkmode-300 bg-opacity-20 hover:bg-opacity-30"
                      href=""
                      @click.prevent="walletStore.openWallet"
                    >
                      <Lucide icon="Plus" class="w-6 h-6" />
                    </a>
                  </div>
                </div>
                <Button
                  v-if="hasDeposit && !loadingCreateVMFlag"
                  variant="primary"
                  class="px-2 py-1 align-top w-full"
                  type="submit"
                  size="lg"
                  :disabled="
                    !(
                      regionSelected &&
                      imageSelected &&
                      flavorSelected &&
                      networkSelected &&
                      networkTypeSelected &&
                      networkOptionSelected
                    )
                  "
                >
                  {{ $t("button.create") }}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
